import { Button, Modal, Textarea } from '@mantine/core';
import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import classes from './NoonReportScreen.module.css';

/**
 * Gerekçeli iptal (ADR 0077 §3, §4): gönderilmiş sefer raporu ya da raporlu sefer silinmez,
 * "Void" olarak izde kalır. Ne olacağı iletişim kutusunda açıkça yazılır; gerekçe istenir. Parola
 * alanı yalnız şirket ayarı "Onayda parolayı her yerde iste" açıkken ya da sunucu isterse görünür
 * (ADR 0075). Geri alınmaz.
 */
export function VoidDialog({
  opened,
  subject,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  /** Raporun mu seferin mi iptali: metinler ona göre. */
  subject: 'report' | 'voyage';
  onClose: () => void;
  /** Parola yalnız girildiyse gelir. */
  onConfirm: (reason: string, password: string | undefined) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.voidDialog.${subject}.${key}`);
  const c = (key: string) => t(`voyage.voidDialog.${key}`);
  const [reason, setReason] = useState('');
  const reauth = useReauth('voyage_report');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setReason('');
    reauth.reset();
    setError(null);
    onClose();
  };
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={k('title')}
      closeButtonProps={{ 'aria-label': c('cancel') }}
    >
      <div className={classes.notice} data-tone="danger" role="note">
        <IconAlertTriangleFilled size={16} aria-hidden />
        <span>
          <strong>{k('lead')}</strong>
          <br />
          {k('body')}
        </span>
      </div>
      <Textarea
        label={c('reason')}
        required
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
        maxLength={1000}
        autosize
        minRows={2}
        mt="sm"
        mb="sm"
      />
      {reauth.shown ? <ReauthField reauth={reauth} label={c('password')} /> : null}
      {error ? (
        <p className={classes.message} data-tone="danger" role="alert">
          {t(`voyage.errors.${error}`, { defaultValue: t('voyage.errors.GENERIC') })}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={close}>
          {c('cancel')}
        </Button>
        <Button
          color="red"
          loading={busy}
          disabled={!reason.trim() || reauth.missing}
          onClick={async () => {
            if (reauth.missing) return;
            setBusy(true);
            const code = await onConfirm(reason.trim(), reauth.body().password);
            setBusy(false);
            if (code) {
              reauth.noteError({ code });
              setError(code);
            } else {
              setReason('');
              reauth.reset();
              setError(null);
            }
          }}
        >
          {k('confirm')}
        </Button>
      </div>
    </Modal>
  );
}
